// shared/src/resolvers/hoist-misnested.ts
// Repair for a DeepSeek dropped-MIDDLE-brace pathology.
//
// When the model drops the closing `}` of an inner sub-object (e.g. `basis`), the
// object is one closer short. parseContent's closeTruncatedObject recovery appends
// the missing `}` at the END (correct for TRAILING truncation, wrong for a middle
// drop), so every field emitted after the drop point ends up NESTED inside that
// sub-object. A non-passthrough `z.object` then STRIPS those keys, so the required
// TOP-LEVEL fields read `undefined` and Zod throws a misleading "N required fields
// missing" SchemaError — which killed brief 019fed0c at m.contrarian.analyst on
// 2026-08-10 (six top-level fields nested into `basis`).
//
// This lifts any sub-object key that is a DECLARED top-level field back to the top
// level, but ONLY when that field is absent up top (never clobbers a real value).
// Correctly-shaped output is untouched (the sub-object holds only its own keys, none
// of which are top-level names → no-op). Pure: returns a shallow-cloned object, never
// mutates the input. Meant to run as a `z.preprocess` BEFORE object validation.
//
// See docs/investigations/m-contrarian-analyst-basis-nesting-2026-08-10.md.

// Minimal console accessor (mirrors llm-parse-content.ts / llm-enum-coerce.ts —
// shared's tsconfig lib excludes the DOM `console` global). Diagnostic-only.
const log = (globalThis as { console?: { warn(...a: unknown[]): void } }).console;

export function hoistMisnestedFields(
  value:        unknown,
  subKey:       string,
  topLevelKeys: readonly string[],
  ctx?:         { step?: string },
): unknown {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return value;
  const src = value as Record<string, unknown>;
  const sub = src[subKey];
  if (sub == null || typeof sub !== 'object' || Array.isArray(sub)) return value;

  const obj    = { ...src };
  const subObj = { ...(sub as Record<string, unknown>) };
  const top    = new Set(topLevelKeys);
  const hoisted: string[] = [];

  for (const k of Object.keys(subObj)) {
    if (k === subKey) continue;                 // never hoist the sub-object onto itself
    if (top.has(k) && obj[k] === undefined) {   // a top-level field misfiled here, absent up top
      obj[k] = subObj[k];
      delete subObj[k];
      hoisted.push(k);
    }
  }

  if (hoisted.length === 0) return value;       // clean output → return the original untouched

  obj[subKey] = subObj;
  log?.warn('[hoist-misnested] lifted misnested fields to top level', {
    sub_key: subKey, hoisted, step: ctx?.step,
  });
  return obj;
}
